<template>
  <section id="overall-trends">
    <article>
      <div class="row clearfix">
        <h1>Overall Trends</h1>
        <div class="sixcol">
          <h2>Key Revenue Insights</h2>
          <ul>
            <li>The variances in revenue reflect the demand for live events and the number of major international tours in any given year.</li>
            <li>Total revenue declined y-o-y by 0.1% from $3.353b in 2024 to $3.351b in 2025.</li>
            <li>Revenue has grown at a compound annual growth rate (CAGR) of 6.9% from 2011 to 2025.</li>
            <li>Circus and Physical Theatre recorded the highest y-o-y increase in revenue (159.6%) among all categories, followed by Opera (48.0%) in 2025.*</li>
            <li>ACT recorded the highest y-o-y growth in revenue (by 26.3% to $38.1m), followed by WA (by 19.1% to $297.2m).</li>
            <li>TAS recorded the highest y-o-y decline in revenue at 5.0%, followed by VIC at 3.8%.</li>
            <li>Metropolitan areas accounted for 89.1% of total revenue, with regional areas contributing 10.6%.**</li>
          </ul>
          <h2>Key Attendance Insights</h2>
          <ul>
            <li>From year to year, attendance at live events varies depending on the shows in market, particularly major international touring acts.</li>
            <li>Total attendance declined y-o-y by 2.4% from 31.4m in 2024 to 30.7m in 2025.</li>
            <li>Attendance has grown at a CAGR of 4.2% from 2011 to 2025.</li>
            <li>TAS recorded the highest y-o-y growth in attendance (26.7%), followed by ACT (8.3%), WA (4.3%) and SA (4.2%).</li>
            <li>NT recorded the highest y-o-y decline in attendance (27.8%), followed by QLD (5.5%), NSW (4.2%) and VIC (3.5%).</li>
            <li>In 2025, Circus and Physical Theatre* recorded the highest y-o-y increase in attendance (88.1%) among all categories, followed by Opera (33.2%) and Festivals (Multi-Category) (10.1%).</li>
            <li>Metropolitan areas accounted for 88.2% of total attendance, with regional areas contributing 11.6%.**</li>
          </ul>
        </div>
        <div class="sixcol last">
          <h2>Key Average Ticket Price Insights</h2>
          <ul>
            <li>The variability in average ticket price reflects the type of shows in market in any given year. Major international acts are typically able to command a higher ticket price.</li>
            <li>The average ticket price increased by 2.4% y-o-y from $115.12 in 2024 to $117.83 in 2025.</li>
            <li>Increased operating costs across the live performance industry have contributed to rising ticket prices.</li>
            <li>The average ticket price has grown at a CAGR of 2.3% from 2011 to 2025.</li>
            <li>In 2025, Circus and Physical Theatre* recorded the highest y-o-y growth in average ticket price, with an increase of 40.3% from $67.93 in 2024 to $95.32 in 2025.</li>
            <li>Classical Music recorded the second-highest y-o-y growth in average ticket price, with an increase of 19.2% from $89.03 in 2024 to $106.10 in 2025.</li>
            <li>Festivals (Contemporary Music), Contemporary Music and Classical Music recorded their highest ever average ticket prices in 2025, at $206.17, $138.15 and $106.10 respectively.</li>
          </ul>
        </div>

        <p style="font-size: .8em;">*Cirque du Soleil’s <em>Luzia</em> was presented in NSW in 2024; however, data for this event were not included in the 2024 report. In 2025, data for Cirque du Soleil’s <em>Corteo</em> have been included, which appears to be a major contributor to the significant growth observed in Circus and Physical Theatre.</p>
        <p style="font-size: .8em;">**Figures may not add to 100% due to a small proportion of revenue and attendance from unidentified venues.</p>
      </div>
    </article>

    <figure class="chart">
      <div class="row clearfix">
        <h2>Total Revenue and Attendance</h2>
        <div class="chart-container">
          <highcharts class="graphcontainer" :options="chartOptions"></highcharts>
          <div class="nb">NB: 2018 figures have been updated to account for an error in the data received.</div>
        </div>
      </div>
    </figure>

    <div class="fullbg">
      <p class="caption">Photo: <em>La Cenerentola</em>, Opera Queensland, Photo Credit: Murray Summerville</p>
      <div class="overlay"></div>
    </div>

  </section>
</template>

<script>
/*eslint-disable */

export default {
  data () {
    return {
      chartOptions: {
        chart: {
          backgroundColor: 'transparent'
        },
        title: {
          text: ''
        },
        xAxis: [{
          categories: [2004, 2005, 2006, 2007, 2008, 2009, 2010, 2011, 2012, 2013, 2014, 2015, 2016, 2017, 2018, 2019, 2020, 2021, 2022, 2023, 2024, 2025],
          labels: {
            style: {
              color: '#fff'
            }
          }
        }],
        yAxis: [{ // Primary yAxis
          min: 0,
          labels: {
            formatter () {
              if (this.value > 999) {
                return '$' + (this.value / 1000) + 'b'
              }
              else {
                return '$' + this.value + 'm'
              }
            },
            style: {
              color: '#fff'
            }
          },
          title: {
            text: 'Gross Revenue',
            style: {
              color: '#fff'
            }
          },
          opposite: true
        }, { // Secondary yAxis
          min: 0,
          labels: {
            format: '{value}m',
            style: {
              color: '#fff'
            }
          },
          title: {
            text: 'Total Attendance',
            style: {
              color: '#fff'
            }
          },
          opposite: false
        }],
        tooltip: {
          shared: true,
          formatter () {
            let revy, tooltipstring, avg;
            let rev = this.points[0];
            let att = this.points[1];
            let atty = att.y.toFixed(2) + 'm';
            let chart = this.points[0].series.chart;
            let categories = chart.xAxis[0].categories;
            let index = 0;
            while (this.x !== categories[index]) {
              index++;
            }
            for (let i=0; i<chart.series.length; i++) {
              let series = chart.series[i];
              avg = series.data[index].y;
            }
            if (rev.y > 1000) {
              revy = '$' + (rev.y / 1000).toFixed(2) + 'b'
            }
            else {
              revy = '$' + rev.y + 'm'
            }
            tooltipstring = this.x + '<br />';
            tooltipstring += '<span style="color:' + att.series.color + '">•</span> Attendance: ' + atty + '<br />'
            tooltipstring += '<span style="color:' + rev.series.color + '">•</span> Revenue: ' + revy + '<br />'
            tooltipstring += '<span>•</span> Avg. Ticket Price: $' + avg.toFixed(2)
            return tooltipstring
          }
        },
        legend: {
          layout: 'vertical',
          align: 'left',
          x: 120,
          verticalAlign: 'top',
          y: 100,
          floating: true,
          backgroundColor: '#fff'
        },
        series: [
          {
            name: 'Revenue',
            type: 'column',
            data: [689.599, 834.337, 1158.065, 1228.659, 1061.273, 1083.33, 1327.806, 1309.187, 1204.884, 1478.977, 1507.964, 1413.209, 1430.4, 1884.142, 2062.425, 1964.642, 605.97, 678.78, 2007.37, 3136.59, 3353.83, 3351.31],
            color: '#3a89eb'
          },
          {
            name: 'Attendance',
            type: 'column',
            yAxis: 1,
            data: [13.477, 15.809, 19.836, 20.887, 15.824, 15.197, 17.241, 17.346, 16.274, 17.927, 18.536, 18.626, 18.783, 23.028, 25.79, 23.864, 7.762, 10.877, 24.227, 30.079, 31.449, 30.705],
            color: '#d14967'
          },
          {
            name: 'Avg ticket Price',
            type: 'line',
            yAxis: 1,
            data: [55.13, 60.62, 64.08, 66.03, 76.6, 80.57, 86.43, 85.99, 85.46, 92.16, 91.57, 86.34, 83.72, 90.59, 96.61, 92.89, 87.14, 69.02, 90.96, 111.48, 115.12, 117.83],
            color: '#cd1f46',
            showInLegend: false,
            visible: false
          }
        ]
      }
    }
  }
}
</script>

<style lang="scss" scoped="scoped">
.chart-container {
  position: relative;
  overflow-x: visible;
  .nb {
    position: absolute;
    color: #fff;
    bottom: -30px;
    right: 10px;
    font-size: 12px;
    font-style: italic;
    font-family: sans-serif;
  }
}
#overall-trends .fullbg {
  background-image: url("~@/assets/img/2025/4-Overall-Trends-Opera-Queensland_La-Cenerentola_MurraySummerville_010325--197.jpg");
}
</style>
